import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

export const Calculator = () => {
  const [formData, setFormData] = useState({
    nome: '',
    dataNascimento: '',
    sexo: '',
    regimePrevidenciario: '',
    atividade: '',
    tempoContribuicao: '',
    mediaContribuicao: '',
  });

  const [result, setResult] = useState(null);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const calculateRetirement = (e) => {
    e.preventDefault();

    // Basic validation
    if (
      !formData.nome ||
      !formData.dataNascimento ||
      !formData.sexo ||
      !formData.regimePrevidenciario ||
      !formData.atividade ||
      !formData.tempoContribuicao ||
      !formData.mediaContribuicao
    ) {
      toast.error('Por favor, preencha todos os campos');
      return;
    }

    // Calculate age
    const birthDate = new Date(formData.dataNascimento);
    const today = new Date();
    const age = today.getFullYear() - birthDate.getFullYear();

    // Mock calculation based on contribution time
    const contributionYears = parseInt(formData.tempoContribuicao);
    const avgContribution = parseFloat(formData.mediaContribuicao);

    // Determine if eligible
    let eligibleStatus = 'not_eligible';
    let requiredYears = 0;
    let estimatedBenefit = 0;

    if (formData.sexo === 'masculino') {
      requiredYears = formData.atividade === 'especial' ? 25 : age >= 65 ? 15 : 35;
    } else {
      requiredYears = formData.atividade === 'especial' ? 25 : age >= 62 ? 15 : 30;
    }

    if (contributionYears >= requiredYears) {
      eligibleStatus = 'eligible';
      // Mock benefit calculation (60% + 2% per year above 20 years for men, 15 for women)
      const baseYears = formData.sexo === 'masculino' ? 20 : 15;
      const additionalYears = Math.max(0, contributionYears - baseYears);
      const percentage = Math.min(100, 60 + additionalYears * 2);
      estimatedBenefit = (avgContribution * percentage) / 100;
    } else {
      const yearsRemaining = requiredYears - contributionYears;
      eligibleStatus = `faltam ${yearsRemaining} ${yearsRemaining === 1 ? 'ano' : 'anos'}`;
    }

    setResult({
      eligible: contributionYears >= requiredYears,
      status: eligibleStatus,
      estimatedBenefit: estimatedBenefit.toFixed(2),
      requiredYears,
      currentYears: contributionYears,
    });

    toast.success('Cálculo realizado com sucesso!');
  };

  const resetForm = () => {
    setFormData({
      nome: '',
      dataNascimento: '',
      sexo: '',
      regimePrevidenciario: '',
      atividade: '',
      tempoContribuicao: '',
      mediaContribuicao: '',
    });
    setResult(null);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section
        className="relative min-h-[350px] flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage:
            'linear-gradient(135deg, rgba(45, 66, 56, 0.9) 0%, rgba(45, 66, 56, 0.8) 100%), url(https://images.unsplash.com/photo-1554224155-6726b3ff858f?q=80&w=2070)',
        }}
      >
        <div className="container-padding mx-auto text-center relative z-10">
          <div className="max-w-3xl mx-auto space-y-4 slide-up">
            <div className="inline-block">
              <i className="fas fa-calculator text-accent text-5xl mb-4"></i>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-primary-foreground">
              Calculadora de Aposentadoria
            </h1>
            <p className="text-lg sm:text-xl text-primary-foreground/90">
              Faça uma simulação completa e descubra quando poderá se aposentar
            </p>
          </div>
        </div>
      </section>

      {/* Calculator Form */}
      <section className="section-padding bg-background">
        <div className="container-padding mx-auto">
          <div className="max-w-4xl mx-auto">
            <Card className="card-hover border-border">
              <CardHeader className="bg-secondary">
                <CardTitle className="font-display text-2xl text-center">
                  <i className="fas fa-file-invoice text-accent mr-2"></i>
                  Preencha seus dados
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 sm:p-8">
                <form onSubmit={calculateRetirement} className="space-y-6">
                  {/* Personal Data */}
                  <div className="space-y-4">
                    <h3 className="font-display text-xl font-semibold text-foreground border-b border-border pb-2">
                      1. Dados Pessoais
                    </h3>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="nome">Seu nome</Label>
                        <Input
                          id="nome"
                          placeholder="Nome Completo"
                          value={formData.nome}
                          onChange={(e) => handleInputChange('nome', e.target.value)}
                          className="border-input"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="dataNascimento">Data de Nascimento</Label>
                        <Input
                          id="dataNascimento"
                          type="date"
                          value={formData.dataNascimento}
                          onChange={(e) => handleInputChange('dataNascimento', e.target.value)}
                          className="border-input"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="sexo">Sexo</Label>
                      <Select
                        value={formData.sexo}
                        onValueChange={(value) => handleInputChange('sexo', value)}
                      >
                        <SelectTrigger className="border-input">
                          <SelectValue placeholder="Selecione o sexo" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="masculino">Masculino</SelectItem>
                          <SelectItem value="feminino">Feminino</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Contribution History */}
                  <div className="space-y-4">
                    <h3 className="font-display text-xl font-semibold text-foreground border-b border-border pb-2">
                      2. Histórico de Contribuição
                    </h3>

                    <div className="space-y-2">
                      <Label htmlFor="regimePrevidenciario">Regime de previdência</Label>
                      <Select
                        value={formData.regimePrevidenciario}
                        onValueChange={(value) => handleInputChange('regimePrevidenciario', value)}
                      >
                        <SelectTrigger className="border-input">
                          <SelectValue placeholder="Selecione o regime" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="rgps">RGPS (INSS)</SelectItem>
                          <SelectItem value="rpps">RPPS (Servidor Público)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="atividade">Tipo de atividade principal</Label>
                      <Select
                        value={formData.atividade}
                        onValueChange={(value) => handleInputChange('atividade', value)}
                      >
                        <SelectTrigger className="border-input">
                          <SelectValue placeholder="Selecione a atividade" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="normal">Atividade Normal</SelectItem>
                          <SelectItem value="especial">Atividade Especial</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="tempoContribuicao">
                          Tempo total de contribuição (anos)
                        </Label>
                        <Input
                          id="tempoContribuicao"
                          type="number"
                          placeholder="Ex: 25"
                          value={formData.tempoContribuicao}
                          onChange={(e) => handleInputChange('tempoContribuicao', e.target.value)}
                          className="border-input"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="mediaContribuicao">
                          Média dos últimos salários de contribuição (R$)
                        </Label>
                        <Input
                          id="mediaContribuicao"
                          type="number"
                          step="0.01"
                          placeholder="Ex: 3500.00"
                          value={formData.mediaContribuicao}
                          onChange={(e) => handleInputChange('mediaContribuicao', e.target.value)}
                          className="border-input"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-4 pt-4">
                    <Button
                      type="submit"
                      size="lg"
                      className="flex-1 bg-accent hover:bg-accent-hover text-accent-foreground font-semibold shadow-lg"
                    >
                      <i className="fas fa-calculator mr-2"></i>
                      Calcular Meu Benefício
                    </Button>
                    <Button
                      type="button"
                      size="lg"
                      variant="outline"
                      onClick={resetForm}
                      className="border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground font-semibold"
                    >
                      <i className="fas fa-redo mr-2"></i>
                      Limpar
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            {/* Results */}
            {result && (
              <Card className="card-hover border-border mt-8 slide-up">
                <CardHeader className="bg-secondary">
                  <CardTitle className="font-display text-2xl text-center">
                    <i className="fas fa-chart-line text-accent mr-2"></i>
                    Resultado da Simulação
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 sm:p-8">
                  <div className="space-y-6">
                    {result.eligible ? (
                      <div className="bg-green-50 border-2 border-green-500 rounded-lg p-6 text-center">
                        <i className="fas fa-check-circle text-green-500 text-5xl mb-4"></i>
                        <h3 className="font-display text-2xl font-bold text-green-700 mb-2">
                          Elegível!
                        </h3>
                        <p className="text-green-600 text-lg">
                          Você já possui os requisitos para se aposentar!
                        </p>
                      </div>
                    ) : (
                      <div className="bg-yellow-50 border-2 border-yellow-500 rounded-lg p-6 text-center">
                        <i className="fas fa-clock text-yellow-500 text-5xl mb-4"></i>
                        <h3 className="font-display text-2xl font-bold text-yellow-700 mb-2">
                          Não Elegível
                        </h3>
                        <p className="text-yellow-600 text-lg">{result.status}</p>
                      </div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div className="bg-secondary p-6 rounded-lg">
                        <div className="text-muted-foreground text-sm mb-2">
                          Tempo de Contribuição Atual
                        </div>
                        <div className="font-display text-3xl font-bold text-primary">
                          {result.currentYears} anos
                        </div>
                      </div>

                      <div className="bg-secondary p-6 rounded-lg">
                        <div className="text-muted-foreground text-sm mb-2">
                          Tempo Necessário
                        </div>
                        <div className="font-display text-3xl font-bold text-primary">
                          {result.requiredYears} anos
                        </div>
                      </div>

                      {result.eligible && (
                        <div className="bg-accent-light p-6 rounded-lg sm:col-span-2">
                          <div className="text-accent text-sm mb-2 font-semibold">
                            Valor Estimado do Benefício
                          </div>
                          <div className="font-display text-4xl font-bold text-accent">
                            R$ {result.estimatedBenefit}
                          </div>
                          <p className="text-xs text-muted-foreground mt-2">
                            *Valor estimado. Procure um especialista para cálculo exato.
                          </p>
                        </div>
                      )}
                    </div>

                    <div className="border-t border-border pt-6">
                      <p className="text-sm text-muted-foreground text-center mb-4">
                        <i className="fas fa-info-circle text-accent mr-2"></i>
                        Esta é uma simulação simplificada. Para um cálculo preciso e personalizado,
                        consulte nossos especialistas.
                      </p>
                      <div className="text-center">
                        <Button
                          asChild
                          size="lg"
                          className="bg-accent hover:bg-accent-hover text-accent-foreground font-semibold shadow-lg"
                        >
                          <a href="/contato">
                            <i className="fas fa-phone-alt mr-2"></i>
                            Falar com um Especialista
                          </a>
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Disclaimer */}
            <div className="mt-8 bg-muted p-6 rounded-lg">
              <h4 className="font-semibold text-foreground mb-2 flex items-center">
                <i className="fas fa-exclamation-triangle text-accent mr-2"></i>
                Atenção!
              </h4>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Este é um cálculo simples e não substitui uma consulta jurídica. A legislação
                previdenciária é complexa e varia conforme cada caso. Entre em contato com nossa
                equipe para uma análise detalhada e personalizada do seu caso.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Calculator;